/**
 * DeleteTenantModal
 *
 * Superadmin-facing modal for the two-step hardened tenant-deletion flow:
 *   1. Fetches a live preview of what would be deleted via
 *      admin_get_tenant_deletion_preview (table-level row counts).
 *   2. User types the tenant name + ticks an "I understand this is
 *      irreversible" acknowledgement.
 *   3. On submit we call admin_request_tenant_deletion to obtain a
 *      short-lived confirmation token, then invoke the admin-delete-tenant
 *      edge function with the token + typed name.
 *   4. Displays the orchestrator's step-by-step result and final summary.
 */

import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Code,
  Divider,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput
} from '@mantine/core'
import {
  IconAlertTriangle,
  IconCheck,
  IconLock,
  IconTrash
} from '@tabler/icons-react'
import { ErrorBoundary } from '@components/ErrorBoundary'

interface Tenant {
  id: string
  name: string | null
  is_master_tenant?: boolean | null
  stripe_subscription_id?: string | null
  stripe_customer_id?: string | null
}

interface PreviewShape {
  tenant: {
    id: string
    name: string
    is_master_tenant: boolean
    owner_user_id: string
    stripe_customer_id: string | null
    stripe_subscription_id: string | null
    retention_policy: Record<string, unknown>
  }
  deletion_state: {
    requested_at: string | null
    grace_until: string | null
    in_progress_at: string | null
  }
  table_counts: Record<string, number>
  total_rows: number
  user_count: number
  user_ids: string[]
}

interface StepReport {
  step: string
  status: 'ok' | 'skipped' | 'partial' | 'error' | 'residual_data'
  [key: string]: unknown
}

interface OrchestrationResult {
  log_id: string | null
  status: 'completed' | 'partial' | 'failed'
  execution_time_ms: number
  total_rows: number
  errors: string[]
  steps: StepReport[]
}

export interface DeleteTenantModalCallbacks {
  /**
   * Fetches the deletion preview (table-level counts etc.) from the backend.
   * Must throw or reject on failure so the modal can surface the error.
   */
  fetchPreview: (tenantId: string) => Promise<PreviewShape>
  /**
   * Performs the full two-step deletion: obtain a confirmation token via
   * `admin_request_tenant_deletion` and invoke the `admin-delete-tenant`
   * edge function. Must throw or reject on failure.
   */
  confirmDeletion: (args: {
    tenantId: string
    typedName: string
    reason: string | null
  }) => Promise<OrchestrationResult>
}

const STEP_ORDER = [
  'stripe_cancel_subscription',
  'stripe_annotate_customer',
  'bookkeeping_archive',
  'notifications_suspend',
  'db_purge',
  'auth_users',
  'storage_purge',
  'post_check',
  'completion_email'
] as const

export function DeleteTenantModal(props: {
  tenant: Tenant
  callbacks: DeleteTenantModalCallbacks
  onClose: () => void
  onDeleted?: (result: OrchestrationResult) => void
}) {
  return (
    <ErrorBoundary
      title="Delete Tenant modal crashed"
      fallback={(err, reset) => (
        <Stack gap="sm">
          <Alert
            color="red"
            icon={<IconAlertTriangle />}
            title="Something went wrong"
          >
            {err.message || String(err)}
          </Alert>
          <Group justify="end">
            <Button variant="default" onClick={reset}>
              Retry
            </Button>
            <Button onClick={props.onClose}>Close</Button>
          </Group>
        </Stack>
      )}
    >
      <DeleteTenantModalBody {...props} />
    </ErrorBoundary>
  )
}

function DeleteTenantModalBody({
  tenant,
  callbacks,
  onClose,
  onDeleted
}: {
  tenant: Tenant
  callbacks: DeleteTenantModalCallbacks
  onClose: () => void
  onDeleted?: (result: OrchestrationResult) => void
}) {
  const [phase, setPhase] = useState<
    'loading_preview' | 'idle' | 'running' | 'done' | 'error'
  >('loading_preview')
  const [preview, setPreview] = useState<PreviewShape | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)

  const [typedName, setTypedName] = useState('')
  const [ackIrreversible, setAckIrreversible] = useState(false)
  const [reason, setReason] = useState('')

  const [runError, setRunError] = useState<string | null>(null)
  const [result, setResult] = useState<OrchestrationResult | null>(null)

  const tenantName = tenant.name ?? ''
  const nameMatches =
    typedName.trim() === tenantName.trim() && tenantName !== ''
  const canSubmit =
    phase === 'idle' &&
    nameMatches &&
    ackIrreversible &&
    !tenant.is_master_tenant

  useEffect(() => {
    if (!tenant?.id) {
      setPreviewError(
        'No tenant selected. Close and reopen the modal from the tenant detail page.'
      )
      setPhase('error')
      return
    }

    let cancelled = false
    setPhase('loading_preview')
    setPreviewError(null)
    setPreview(null)
    ;(async () => {
      try {
        const shaped = await callbacks.fetchPreview(tenant.id)
        if (cancelled) return
        if (!shaped || typeof shaped !== 'object' || !shaped.tenant) {
          setPreviewError(
            'Preview response was malformed. The server returned no tenant data.'
          )
          setPhase('error')
          return
        }
        setPreview(shaped)
        setPhase('idle')
      } catch (e) {
        if (cancelled) return
        const msg = e instanceof Error ? e.message : String(e)
        console.error('[DeleteTenantModal] preview fetch failed:', e)
        setPreviewError(msg)
        setPhase('error')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [tenant?.id, callbacks])

  const sortedTableCounts = useMemo(() => {
    if (!preview?.table_counts) return []
    return Object.entries(preview.table_counts)
      .filter(([, n]) => n > 0)
      .sort((a, b) => b[1] - a[1])
  }, [preview])

  const runDeletion = async () => {
    if (!tenant.id || !nameMatches) return
    setRunError(null)
    setPhase('running')

    try {
      const raw = await callbacks.confirmDeletion({
        tenantId: tenant.id,
        typedName: typedName.trim(),
        reason: reason.trim() || null
      })

      const orchestrationResult: OrchestrationResult = {
        log_id: raw?.log_id ?? null,
        status: raw?.status ?? 'failed',
        execution_time_ms: raw?.execution_time_ms ?? 0,
        total_rows: raw?.total_rows ?? 0,
        errors: Array.isArray(raw?.errors) ? raw.errors : [],
        steps: Array.isArray(raw?.steps) ? raw.steps : []
      }
      setResult(orchestrationResult)
      setPhase('done')
      onDeleted?.(orchestrationResult)
    } catch (e) {
      console.error('[DeleteTenantModal] deletion failed:', e)
      setRunError(e instanceof Error ? e.message : String(e))
      setPhase('error')
    }
  }

  // -----------------------------------------------------------
  // Rendering
  // -----------------------------------------------------------
  if (phase === 'loading_preview') {
    return (
      <Stack align="center" py="lg">
        <Loader />
        <Text>Loading deletion preview…</Text>
      </Stack>
    )
  }

  if (phase === 'error' && !result) {
    const isPreviewError = previewError != null
    return (
      <Stack c="black">
        <Alert
          color="red"
          icon={<IconAlertTriangle />}
          title={isPreviewError ? 'Preview failed' : 'Deletion failed'}
        >
          {previewError ?? runError ?? 'Unknown error'}
        </Alert>
        <Group justify="end">
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
        </Group>
      </Stack>
    )
  }

  if (phase === 'done' && result) {
    return <ReportPanel result={result} onClose={onClose} />
  }

  // idle / running
  return (
    <Stack c="white" gap="md">
      <Alert
        icon={<IconAlertTriangle />}
        color="red"
        title="This action is IRREVERSIBLE"
        variant="filled"
      >
        All tenant data will be permanently destroyed{' '}
        <strong>immediately</strong> — the superadmin path does not honour the
        30-day recovery window. Restore is only possible via Supabase
        point-in-time recovery.
      </Alert>

      {tenant.is_master_tenant && (
        <Alert color="red" icon={<IconLock />}>
          This is the <strong>master tenant</strong>. It cannot be deleted.
        </Alert>
      )}

      {preview && (
        <>
          <Divider
            label={`Tenant: ${preview.tenant.name}`}
            labelPosition="left"
          />

          <Group gap="xs" wrap="wrap">
            <Badge size="lg" color="red">
              {preview.user_count} user
              {preview.user_count === 1 ? '' : 's'}
            </Badge>
            <Badge size="lg" color="red" variant="light">
              {preview.total_rows.toLocaleString()} total rows
            </Badge>
            {preview.tenant.stripe_subscription_id && (
              <Badge color="yellow" variant="light">
                Stripe subscription will be cancelled
              </Badge>
            )}
            {preview.tenant.stripe_customer_id && (
              <Badge color="gray" variant="light">
                Stripe customer annotated (kept)
              </Badge>
            )}
          </Group>

          <ScrollArea h={220} type="auto" offsetScrollbars>
            <Table c={'white'} withTableBorder withColumnBorders fz="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Table</Table.Th>
                  <Table.Th ta="right">Rows to delete</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sortedTableCounts.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={2}>
                      <Text c="dimmed" ta="center">
                        No data rows found.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  sortedTableCounts.map(([table, count]) => (
                    <Table.Tr key={table}>
                      <Table.Td>{table}</Table.Td>
                      <Table.Td ta="right">{count.toLocaleString()}</Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </>
      )}

      <Stack c={'white'}>
        <TextInput
          label={`Type the tenant name to confirm: ${tenantName}`}
          placeholder={tenantName}
          value={typedName}
          onChange={(e) => setTypedName(e.currentTarget.value)}
          error={typedName && !nameMatches ? 'Name does not match' : undefined}
          disabled={phase === 'running'}
          autoComplete="off"
        />

        <TextInput
          label="Reason (appended to audit log)"
          placeholder="Why are you deleting this tenant?"
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          disabled={phase === 'running'}
        />

        <Checkbox
          checked={ackIrreversible}
          onChange={(e) => setAckIrreversible(e.currentTarget.checked)}
          label="I understand this is irreversible and I have operational authority to proceed."
          disabled={phase === 'running'}
        />

        {runError && (
          <Alert color="red" icon={<IconAlertTriangle />}>
            {runError}
          </Alert>
        )}

        <Group justify="end" mt="sm">
          <Button
            variant="default"
            onClick={onClose}
            disabled={phase === 'running'}
          >
            Cancel
          </Button>
          <Button
            color="red"
            leftSection={
              phase === 'running' ? <Loader size="xs" /> : <IconTrash />
            }
            disabled={!canSubmit}
            loading={phase === 'running'}
            onClick={runDeletion}
          >
            Delete Tenant Permanently
          </Button>
        </Group>
      </Stack>
    </Stack>
  )
}

// ---------------------------------------------------------------------------
// Post-run report panel
// ---------------------------------------------------------------------------
function ReportPanel({
  result,
  onClose
}: {
  result: OrchestrationResult
  onClose: () => void
}) {
  const byStep = new Map(result.steps.map((s) => [s.step, s]))
  // Render known steps in canonical order, then any steps the orchestrator
  // reported that are not (yet) in STEP_ORDER — this way we never silently
  // drop information the backend sent.
  const orderedStepNames: string[] = [
    ...STEP_ORDER.filter((name) => byStep.has(name)),
    ...result.steps
      .map((s) => s.step)
      .filter(
        (name) => !STEP_ORDER.includes(name as (typeof STEP_ORDER)[number])
      )
  ]
  const statusColor =
    result.status === 'completed'
      ? 'green'
      : result.status === 'partial'
        ? 'yellow'
        : 'red'

  return (
    <Stack gap="md">
      <Alert
        icon={<IconCheck />}
        color={statusColor}
        variant="filled"
        title={
          result.status === 'completed'
            ? 'Tenant deleted successfully'
            : result.status === 'partial'
              ? 'Tenant deleted with warnings'
              : 'Tenant deletion failed'
        }
      >
        <Text size="sm">
          {result.total_rows.toLocaleString()} rows removed in{' '}
          {result.execution_time_ms}ms.{' '}
          {result.log_id ? (
            <Code bg={'transparent'}>Log: {result.log_id}</Code>
          ) : null}
        </Text>
      </Alert>

      <Divider label="Steps" labelPosition="left" />
      <Table withTableBorder fz="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Step</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Detail</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {orderedStepNames.map((stepName) => {
            const s = byStep.get(stepName)
            if (!s) return null
            return (
              <Table.Tr key={stepName}>
                <Table.Td>{stepName}</Table.Td>
                <Table.Td>
                  <Badge
                    color={
                      s.status === 'ok'
                        ? 'green'
                        : s.status === 'skipped'
                          ? 'gray'
                          : s.status === 'partial'
                            ? 'yellow'
                            : 'red'
                    }
                    variant="light"
                  >
                    {s.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Code block fz="10px" bg={'transparent'}>
                    {JSON.stringify(
                      Object.fromEntries(
                        Object.entries(s).filter(
                          ([k]) => k !== 'step' && k !== 'status'
                        )
                      ),
                      null,
                      2
                    )}
                  </Code>
                </Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table>

      {result.errors.length > 0 && (
        <Alert color="red" icon={<IconAlertTriangle />} title="Errors">
          <Stack gap={4}>
            {result.errors.map((e, i) => (
              <Code key={i} block>
                {e}
              </Code>
            ))}
          </Stack>
        </Alert>
      )}

      <Box mt="sm">
        <Group justify="end">
          <Button onClick={onClose}>Close</Button>
        </Group>
      </Box>
    </Stack>
  )
}
